import { useState, useEffect, useMemo } from 'react';
import { getCollectionThoughts } from '../services/collectionService';

/**
 * Custom hook to fetch thoughts for a collection with loading state
 * 
 * @param {string} collectionId - The ID of the collection to fetch thoughts for
 * @param {boolean} isDefault - Whether this is the default collection
 * @param {Array} allThoughts - All thoughts (used for default collection)
 * @returns {Object} - The collection thoughts state and loading state
 */
const useCollectionThoughts = (collectionId, isDefault = false, allThoughts = []) => {
  const [collectionThoughts, setCollectionThoughts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize allThoughts to prevent unnecessary re-renders
  const memoizedAllThoughts = useMemo(() => allThoughts, [allThoughts]);

  useEffect(() => {
    const fetchCollectionThoughts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // If this is the default collection, just use all thoughts
        if (isDefault) {
          setCollectionThoughts(memoizedAllThoughts);
          setLoading(false);
          return;
        }
        
        // Otherwise fetch the collection thoughts
        const fetchedThoughts = await getCollectionThoughts(collectionId);
        setCollectionThoughts(fetchedThoughts);
      } catch (error) {
        console.error('Error fetching collection thoughts:', error);
        setCollectionThoughts([]);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCollectionThoughts();
  }, [collectionId, isDefault, memoizedAllThoughts]);

  return {
    collectionThoughts,
    loading,
    error,
    setCollectionThoughts
  };
};

export default useCollectionThoughts; 